import {
    ComboInput,
    ComboboxContainer,
    DropdownButton,
    DropdownItem,
    DropdownItemLabel,
    DropdownList,
    InputWrapper,
    LoadingIndicator,
    MetaText,
    NoResults,
    SelectedDisplay,
    SelectedDisplayLabel,
} from './ComboboxDropdown.styles';
import { FieldGroup } from '../styles/components/forms.styles';
import { HintText, Label } from '../styles/components/primitives.styles';
import React, { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingBall } from '@ingka/loading';
import ReactDOM from 'react-dom';
import SSRIcon from '@ingka/ssr-icon';
import chevronDownSmallIcon from '@ingka/ssr-icon/paths/chevron-down-small';

export interface ComboboxOption {
    id: string;
    displayName: string;
    hasData?: boolean;
    meta?: ReactNode;
}

interface ComboboxDropdownProps {
    label: string;
    value: string | null;
    options: ComboboxOption[];
    onChange: (id: string | null) => void;
    onDirectInput?: (id: string) => void;
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    allowDirectInput?: boolean;
    directInputPattern?: RegExp;
    directInputHint?: string;
    hint?: string;
}

export const ComboboxDropdown: React.FC<ComboboxDropdownProps> = ({
    label,
    value,
    options,
    onChange,
    onDirectInput,
    disabled = false,
    loading = false,
    placeholder = 'Select or type ID...',
    allowDirectInput = true,
    directInputPattern,
    directInputHint,
    hint,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [showPatternHint, setShowPatternHint] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find((opt) => opt.id === value);
    const showSelectedDisplay = !!(selectedOption && !isTyping);
    const displayValue = selectedOption?.displayName || value || '';

    useEffect(() => {
        setInputValue(displayValue);
    }, [displayValue]);

    const updateCoords = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownCoords({ top: rect.bottom, left: rect.left, width: rect.width });
        }
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setIsTyping(false);
        setShowPatternHint(false);
    };

    const filteredOptions = useMemo(() => {
        if (!isTyping || !inputValue) return options;
        const lower = inputValue.toLowerCase();
        return options.filter(
            (opt) =>
                opt.id.toLowerCase().includes(lower) ||
                opt.displayName.toLowerCase().includes(lower)
        );
    }, [inputValue, options, isTyping]);

    const matchesDirectPattern = (v: string) => (directInputPattern ?? /.+/).test(v);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        setIsTyping(true);
        setIsOpen(true);
        setHighlightedIndex(-1);
        setShowPatternHint(false);
    };

    const handleSelectOption = (option: ComboboxOption) => {
        setInputValue(option.displayName);
        setHighlightedIndex(-1);
        onChange(option.id);
        closeDropdown();
    };

    const handleDirectSubmit = () => {
        const trimmed = inputValue.trim();

        if (!trimmed) {
            onChange(null);
            closeDropdown();
            return;
        }

        const existing = options.find(
            (opt) => opt.id === trimmed || opt.displayName.toLowerCase() === trimmed.toLowerCase()
        );

        if (existing) {
            onChange(existing.id);
            setInputValue(existing.displayName);
            closeDropdown();
            return;
        }

        if (allowDirectInput && onDirectInput && matchesDirectPattern(trimmed)) {
            onDirectInput(trimmed);
            closeDropdown();
            return;
        }

        setShowPatternHint(true);
        setIsTyping(true);
        setIsOpen(true);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) { updateCoords(); setIsOpen(true); }
                else setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelectOption(filteredOptions[highlightedIndex]);
                } else {
                    handleDirectSubmit();
                }
                break;
            case 'Escape':
                closeDropdown();
                setInputValue(displayValue);
                break;
            case 'Tab':
                if (isOpen) handleDirectSubmit();
                break;
        }
    };

    const handleFocus = () => {
        updateCoords();
        setIsTyping(true);
        setInputValue('');
        setIsOpen(true);
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (containerRef.current?.contains(e.relatedTarget as Node)) return;
        setInputValue(displayValue);
    };

    const handleDropdownClick = () => {
        if (disabled || loading) return;
        updateCoords();
        const nextIsOpen = !isOpen;
        if (nextIsOpen) {
            setIsTyping(false);
            setInputValue('');
        }
        setIsOpen(nextIsOpen);
        inputRef.current?.focus();
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const isInsideContainer = containerRef.current?.contains(target);
            const isInsideDropdown = dropdownRef.current?.contains(target);
            if (!isInsideContainer && !isInsideDropdown) {
                closeDropdown();
            }
        };

        const handleScroll = () => updateCoords();

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    const noResultsText = useMemo(() => {
        const trimmed = inputValue.trim();
        if (!trimmed) return null;
        if (allowDirectInput && (directInputPattern ?? /.+/).test(trimmed)) return `Press Enter to search for "${trimmed}"`;
        if (allowDirectInput && showPatternHint && directInputHint) return directInputHint;
        return null;
    }, [inputValue, allowDirectInput, directInputPattern, showPatternHint, directInputHint]);

    return (
        <FieldGroup>
            <Label>
                {label}
                {loading && (
                    <LoadingIndicator>
                        <LoadingBall size="small" color="primary" />
                    </LoadingIndicator>
                )}
            </Label>
            <ComboboxContainer ref={containerRef}>
                <InputWrapper>
                    <ComboInput
                        $hideText={showSelectedDisplay}
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        disabled={disabled || loading}
                        placeholder={loading ? 'Loading...' : placeholder}
                    />
                    {showSelectedDisplay && selectedOption && (
                        <SelectedDisplay>
                            <SelectedDisplayLabel>{selectedOption.displayName}</SelectedDisplayLabel>
                            {selectedOption.meta && <MetaText>{selectedOption.meta}</MetaText>}
                        </SelectedDisplay>
                    )}
                    <DropdownButton
                        type="button"
                        onClick={handleDropdownClick}
                        disabled={disabled || loading}
                        tabIndex={-1}
                        aria-label="Toggle dropdown"
                    >
                        <SSRIcon paths={chevronDownSmallIcon} />
                    </DropdownButton>
                </InputWrapper>
            </ComboboxContainer>
            {hint && <HintText>{hint}</HintText>}
            {isOpen && !disabled && ReactDOM.createPortal(
                <DropdownList
                    ref={dropdownRef}
                    $top={dropdownCoords.top}
                    $left={dropdownCoords.left}
                    $width={dropdownCoords.width}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <DropdownItem
                                key={option.id}
                                $highlighted={index === highlightedIndex}
                                $hasData={option.hasData}
                                onClick={() => handleSelectOption(option)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                <DropdownItemLabel>{option.displayName}</DropdownItemLabel>
                                {option.meta && <MetaText>{option.meta}</MetaText>}
                            </DropdownItem>
                        ))
                    ) : noResultsText ? (
                        <NoResults>{noResultsText}</NoResults>
                    ) : null}
                </DropdownList>,
                document.body
            )}
        </FieldGroup>
    );
};

export default ComboboxDropdown;
